import { useState } from 'react';
import { Card, CardContent, InnerCardHeader } from '@/components/ui/Card.tsx';
import { InstallmentSummaryDto } from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import PaymentStatusIndicator from '@/features/public/bookingSummary/components/PaymentStatusIndicator.tsx';
import { formatPrice } from '@/utils/formatters.ts';
import timeUtils from '@/utils/timeUtils.ts';
import { paymentMethodLabels } from '@/features/dashboard/tourConfig/types/PaymentMethod.utils.ts';
import Chip from '@/components/ui/Chip.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { useInitiatePublicPayment } from '@/features/public/bookingSummary/hooks/useInitiatePublicPayment.ts';

interface InstallmentItemProps {
  installment: InstallmentSummaryDto;
  index: number;
  orgSlug: string;
  token: string;
}

const getInstallmentStatus = (
  transactions: InstallmentSummaryDto['transactions'],
): PlannedTransactionPaymentStatus => {
  const allPaid = transactions.every(
    (t) => t.paymentStatus === PlannedTransactionPaymentStatus.PAID,
  );
  if (allPaid) return PlannedTransactionPaymentStatus.PAID;

  const anyPending = transactions.some(
    (t) => t.paymentStatus === PlannedTransactionPaymentStatus.AWAITING_PAYMENT,
  );
  if (anyPending) return PlannedTransactionPaymentStatus.AWAITING_PAYMENT;

  return (
    transactions[0]?.paymentStatus ??
    PlannedTransactionPaymentStatus.AWAITING_PAYMENT
  );
};

const isPayable = (status: PlannedTransactionPaymentStatus) =>
  status === PlannedTransactionPaymentStatus.AWAITING_PAYMENT ||
  status === PlannedTransactionPaymentStatus.OVERDUE;

const InstallmentItem = ({
  installment,
  index,
  orgSlug,
  token,
}: InstallmentItemProps) => {
  const installmentStatus = getInstallmentStatus(installment.transactions);
  const [payingTransactionId, setPayingTransactionId] = useState<string | null>(
    null,
  );
  const { mutate, isPending } = useInitiatePublicPayment();

  const hasTpay = installment.allowedPaymentMethods.includes(
    PaymentMethod.TPAY,
  );

  const handlePay = (plannedTransactionId: string) => {
    setPayingTransactionId(plannedTransactionId);
    mutate({
      orgSlug,
      token,
      plannedTransactionId,
      provider: 'TPAY',
      successUrl: window.location.href,
      errorUrl: window.location.href,
    });
  };

  return (
    <Card>
      <InnerCardHeader className="py-2">
        <div className="flex justify-between gap-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">Rata {index + 1}</p>
            <PaymentStatusIndicator status={installmentStatus} />
          </div>
          <span className="text-sm font-medium">
            {timeUtils.toDateString(installment.dueDate)}
          </span>
        </div>
      </InnerCardHeader>
      <CardContent className="space-y-2 p-3 py-2">
        {/* Trzy metody płatności nie mieszczą się obok etykiety na telefonie
            i rozpychały całą stronę w poziomie — zawijamy zamiast przewijać. */}
        <div className="flex flex-row flex-wrap justify-between gap-1">
          <div className="text-sm text-neutral-700">
            Akceptowana forma płatności
          </div>
          <div className="flex flex-row flex-wrap gap-1">
            {installment.allowedPaymentMethods.map((method) => (
              <Chip
                key={method}
                label={paymentMethodLabels[method] ?? method}
                tone="neutral"
                fill="soft"
              />
            ))}
          </div>
        </div>
        {installment.transactions.map((transaction) => (
          <div
            key={transaction.plannedTransactionId}
            className="flex items-center justify-between rounded-md bg-neutral-50 p-3"
          >
            <span className="text-sm font-medium">
              {formatPrice(
                transaction.amount.amount,
                transaction.amount.currency,
              )}
            </span>
            <div className="flex items-center gap-2">
              <PaymentStatusIndicator status={transaction.paymentStatus} />
              {hasTpay && isPayable(transaction.paymentStatus) && (
                <ActionButton
                  tone="accent"
                  size="xs"
                  isLoading={
                    isPending &&
                    payingTransactionId === transaction.plannedTransactionId
                  }
                  onClick={() => handlePay(transaction.plannedTransactionId)}
                >
                  Zapłać
                </ActionButton>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default InstallmentItem;
